import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Infer } from 'superstruct';

import { createSuccessToast } from '@adflow/frontend/ui-toasts';
import { apiRequest, handleRequestErrors } from '@adflow/frontend/util-query';
import type {
	ListMediaQueryDto,
	MediaDestination,
} from '@adflow/shared/data-media';
import {
	AddFacebookMedia,
	AddGMBMedia,
	AddGoogleAdsMedia,
	DeleteFacebookMedia,
	DeleteGMBMedia,
	DeleteGoogleAdsMedia,
	EditFacebookMedia,
	EditGMBMedia,
	EditGoogleAdsMedia,
	GetFacebookMedia,
	ListFacebookMedias,
	ListGMBMedias,
	ListGoogleAdsMedias,
	ProcessFacebookMedia,
	ProcessGMBMedia,
	ProcessGoogleAdsMedia,
} from '@adflow/shared/data-media';

const keys = {
	all: ['media'],

	allLists: () => [...keys.all, 'list'] as const,

	list: (query: ListMediaQueryDto, destination: MediaDestination) =>
		[...keys.allLists(), query, destination] as const,

	view: (mediaId: number) => [...keys.all, 'view', mediaId] as const,
};

export function useQueryMedias(
	query: ListMediaQueryDto,
	enabled = true,
	destination: MediaDestination
) {
	return useQuery({
		queryKey: keys.list(query, destination),
		queryFn: ({ signal }) =>
			apiRequest(
				destination === 'FACEBOOK'
					? ListFacebookMedias
					: destination === 'GMB'
						? ListGMBMedias
						: ListGoogleAdsMedias,
				{ query },
				{ signal }
			),
		enabled,
	});
}

export function useQueryFacebookMedia(mediaId: number) {
	return useQuery({
		queryKey: keys.view(mediaId),
		queryFn: ({ signal }) =>
			apiRequest(GetFacebookMedia, { params: { mediaId } }, { signal }),
	});
}

export function addMedia(destination: MediaDestination) {
	switch (destination) {
		case 'FACEBOOK': {
			return addFacebookMedia;
		}
		case 'GOOGLE': {
			return addGoogleAdsMedia;
		}
		case 'GMB': {
			return addGMBMedia;
		}
		default: {
			throw new Error(`Unsupported media destination: ${destination}`);
		}
	}
}

// Not using a mutation because this will be called in parallel for multiple files
async function addFacebookMedia(
	body: Infer<typeof AddFacebookMedia.req>['body'],
	signal: AbortSignal
) {
	return await apiRequest(AddFacebookMedia, { body }, { signal });
}

async function addGoogleAdsMedia(
	body: Infer<typeof AddFacebookMedia.req>['body'],
	signal: AbortSignal
) {
	return await apiRequest(AddGoogleAdsMedia, { body }, { signal });
}

async function addGMBMedia(
	body: Infer<typeof AddGMBMedia.req>['body'],
	signal: AbortSignal
) {
	return await apiRequest(AddGMBMedia, { body }, { signal });
}

export function processMedia(destination: MediaDestination) {
	switch (destination) {
		case 'FACEBOOK': {
			return processFacebookMedia;
		}
		case 'GOOGLE': {
			return processGoogleAdsMedia;
		}
		case 'GMB': {
			return processGMBMedia;
		}
		default: {
			throw new Error(`Unsupported media destination: ${destination}`);
		}
	}
}

// Not using a mutation because this will be called in parallel for multiple files
async function processFacebookMedia(mediaId: number) {
	return await apiRequest(ProcessFacebookMedia, { params: { mediaId } });
}

async function processGoogleAdsMedia(mediaId: number) {
	return await apiRequest(ProcessGoogleAdsMedia, { params: { mediaId } });
}

async function processGMBMedia(mediaId: number) {
	return await apiRequest(ProcessGMBMedia, { params: { mediaId } });
}

export function useInvalidateListMedias() {
	const queryClient = useQueryClient();

	return useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: keys.allLists() });
	}, [queryClient]);
}

interface EditMediaParams {
	mediaId: number;
	body: Infer<typeof EditFacebookMedia.req>['body'];
}
export function useMutationEditMedia(destination: MediaDestination) {
	const queryClient = useQueryClient();
	const { t } = useTranslation('media');

	const route =
		destination === 'FACEBOOK'
			? EditFacebookMedia
			: destination === 'GOOGLE'
				? EditGoogleAdsMedia
				: EditGMBMedia;

	return useMutation({
		mutationFn: ({ mediaId, body }: EditMediaParams) =>
			apiRequest(route, { params: { mediaId }, body }),

		onSuccess: (_res, { mediaId }) => {
			createSuccessToast(t('toast.save.success'));
			void queryClient.invalidateQueries({ queryKey: keys.allLists() });
			void queryClient.invalidateQueries({ queryKey: keys.view(mediaId) });
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('toast.save.error'),
			});
		},
	});
}

export function useMutationDeleteMedia(destination: MediaDestination) {
	const queryClient = useQueryClient();
	const { t } = useTranslation('media');

	const route =
		destination === 'FACEBOOK'
			? DeleteFacebookMedia
			: destination === 'GOOGLE'
				? DeleteGoogleAdsMedia
				: DeleteGMBMedia;

	return useMutation({
		mutationFn: (mediaId: number) => apiRequest(route, { params: { mediaId } }),

		onSuccess: (_res, mediaId) => {
			createSuccessToast(t('toast.delete.success'));
			void queryClient.invalidateQueries({ queryKey: keys.allLists() });
			void queryClient.invalidateQueries({ queryKey: keys.view(mediaId) });
		},
		onError: (err) => {
			handleRequestErrors([err], {
				errorMessage: t('toast.delete.error'),
			});
		},
	});
}

// export function useMutationArchiveMedia(destination: MediaDestination) {
// 	const queryClient = useQueryClient();

// 	const route =
// 		destination === 'FACEBOOK' ? ArchiveFacebookMedia : ArchiveGoogleAdsMedia;

// 	return useMutation({
// 		mutationFn: (mediaId: number) => apiRequest(route, { params: { mediaId } }),

// 		onSuccess: (_res, mediaId) => {
// 			void queryClient.invalidateQueries({ queryKey: keys.allLists() });
// 			void queryClient.invalidateQueries({ queryKey: keys.view(mediaId) });
// 		},
// 	});
// }
